// "Home": dusk in the temple courtyard → the flower-seller leaves her lotus basket → look up to the Panchshul, ring a bell, the aarti → gangajal washes it into the painting.
export const home = {
  slug: "home",
  duration: 30,
  music: "home-dusk",
  music2: [13.2, "home-aarti"],
  captionTail: "Painted with gangajal, on Shivratri.",
  stages: [
    { id: "lanes", at: 0, hint: "the lanes of Baba Dham" },
    { id: "courtyard", at: 5.5, hint: "dusk in the courtyard" },
    { id: "spot", at: 7, hint: "beside the Hriday Peeth" },
    { id: "aarti", at: 12.5, hint: "look up · tap to ring a bell" },
    { id: "gangajal", at: 23, hint: "the jalabhishek" },
  ],
  hold: { at: 13, need: 0.05, auto: 6 },
  touch: { kind: "bell", from: 12.5, until: 23 },
  // her line appears once, at the end, below the painting after the fly-in
  line: { text: "Faith is a homecoming for the soul.", from: 27.6, to: 29.5, pos: "low" },
  ambient: { scene: "reveal", until: 99, then: [] },
  // a Sama Veda chant under the bells, conch, dhaak and aarti: soft and spacious on arrival, fuller at the aarti peak, echoing into the jalabhishek
  bed: { src: "/sounds/home/vedic-chant.mp3", gain: [[0, 0], [2.5, 0.1], [7, 0.14], [12.5, 0.16], [14.5, 0.3], [19, 0.34], [23, 0.26], [27, 0.16], [31, 0.06], [36, 0.0]] },
  // every stage surrounds the visitor: painted wrap panoramas (lanes → courtyard at dusk → the aarti), with foreground garlands and lamps, drifting camphor smoke and petals falling close to the eye for depth
  layers: [
    { src: "/arcs/home/lanes.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, glow: 0.08, show: [-1, 0, 4.8, 7.6] },
    { src: "/arcs/home/courtyard.webp", wrap: 3.48, th: 0, h: 15, y: 5.1, r: 4.9, glow: 0.06, mist: 0.06, mistCol: "#3c3f72", show: [4.8, 7.6, 12.8, 15] },
    { src: "/arcs/home/aarti.webp", wrap: 3.48, th: 0, h: 15, y: 5.1, r: 4.9, glow: 0.14, shimmer: 0.2, mist: [[13, 0.04], [23, 0.06], [27, 0.3]], mistCol: "#f3e2c4", show: [12.8, 15, 99, 99] },
    // camphor and incense smoke drifting across the frame, lit by the lamps
    { src: "/arcs/home/smoke.webp", add: true, th: -0.1, y: 2.4, w: 9.5, h: 6.4, r: 3.6, opacity: [[5, 0.35], [13, 0.5], [20, 0.6], [26, 0.3]], drift: [5, 30, 0.35, 0.5], show: [5, 8, 26, 29] },
    // the nearest layer: marigold swags above and brass diyas with devotees' raised hands below, close to the eye
    { src: "/arcs/home/fg.webp", black: true, flicker: 0.12, th: 0, y: 1.75, w: 7.2, h: 4.8, r: 2.7, opacity: 0.9, show: [6.2, 8.6, 12.4, 13.6] },
    // marigold and rose petals falling in front of the camera
    { src: "/arcs/home/petals.webp", black: true, th: 0.15, y: 3.2, w: 1.7, h: 1.7, r: 2.4, opacity: 0.85, drift: [6, 28, -3.2, -0.3], show: [6.5, 8.5, 26, 28] },
    // the jalabhishek: painted hands tilt the copper kalash; the gangajal catches the lamp light and splashes softly as it lands, then washes forward into the fly-in
    { src: "/arcs/home/jalabhishek.webp", black: true, shimmer: 0.4, glow: 0.22, th: 0.02, y: 2.05, w: 2.5, h: 3.72, r: 3.3, show: [22.4, 23.6, 27, 28.6] },
  ],
  // the aarti peak: the dhaak, the conch and bells over the sung aarti and chants (the music2 track)
  sounds: ["/sounds/home/dhak.mp3", "/sounds/home/conch.mp3", "/sounds/home/bell.mp3"],
  cues: [[1.2, "bellSoft"], [13.4, "dhak"], [14.2, "conch"], [16.5, "bellSoft"], [18.5, "dhak"], [19.2, "bellSoft"], [21.5, "bellSoft"], [23.2, "bellSoft"]],
  // a slow cinematic drift, as if standing in the courtyard and turning to take it in
  look: { start: 0.02, lift: [13.2, 16.5], end: 0.4, yaw: [[0, -0.6], [5, 0.5], [8, 0.3], [12, 0], [17, 0.12], [22, -0.08], [30, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "temple", pos: "low" },
  speed: [[0, 1], [30, 1]],
  figures: {
    appear: [0, 1.5],
    thread: [99, 99],
    fade: [24, 27.5],
    list: [
      { src: "/arcs/home/lady.webp", th: 0.2, y: 1.2, w: 0.95, h: 2.38, appear: [7.2, 8.6], fade: [12.2, 13.6] },
      { src: "/arcs/home/basket.webp", th: 0.04, y: 0.38, w: 0.82, h: 0.74, appear: [8.6, 9.6] },
    ],
  },
  figFrag: `
uniform sampler2D uFig;
uniform float uAppear, uFade, uT, uSoft;
varying vec2 vUv;
void main(){
  vec4 c = texture2D(uFig, vUv);
  float edge = uSoft > 0.5 ? smoothstep(0.0, 0.24, vUv.x) * smoothstep(0.0, 0.24, 1.0 - vUv.x) * smoothstep(0.04, 0.2, vUv.y) * smoothstep(0.0, 0.12, 1.0 - vUv.y) : 1.0;
  float a = c.a * edge * uAppear * (1.0 - uFade);
  if (a < 0.01) discard;
  gl_FragColor = vec4(c.rgb, a);
  #include <colorspace_fragment>
}`,
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface;
varying vec3 vPos;
${"__NOISE__"}
float hash(float n){ return fract(sin(n * 127.1) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
// a diya flame: a small teardrop of light
float flame(vec2 p){
  p.y -= 0.02;
  return 1.0 - smoothstep(0.85, 1.0, length(vec2(p.x / (0.035 * (1.0 - clamp(p.y / 0.08, -1.0, 1.0) * 0.7)), p.y / 0.08)));
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float n2 = snoise(vPos * 3.0 + 4.0) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float aarti = smoothstep(13.2, 15.0, uT);
  float wash = smoothstep(23.0, 25.0, uT);
  // the painting appears only at the end, through the fly-in (never tiled onto the courtyard)
  float settle = 0.0;
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float X = th * uRadius;
    float y = vPos.y;
    col = mix(vec3(0.42, 0.48, 0.72), vec3(0.2, 0.27, 0.55), smoothstep(1.5, 9.0, y)) + 0.03 * n;
    // the courtyard's whitewashed compound, with arched niches
    float wallTop = 1.75 + 0.08 * step(0.5, fract(X / 2.4));
    float wall = step(y, wallTop);
    vec2 ac = vec2(fract(X / 1.2) - 0.5, y - 0.55);
    float niche = step(abs(ac.x), 0.22) * step(ac.y, 0.45 - ac.x * ac.x * 4.0) * step(-0.55, ac.y);
    vec3 lime = mix(vec3(0.5, 0.47, 0.5), vec3(0.58, 0.54, 0.56), n2) + vec3(0.14, 0.07, 0.01) * (1.0 - smoothstep(0.6, 1.7, y));
    col = mix(col, mix(lime, vec3(0.32, 0.2, 0.16), niche * 0.85), wall);
    // marigold garlands swagged along the wall, ruffled petals in orange and yellow
    float u = fract(X / 1.2) * 2.0 - 1.0;
    float gy = 1.5 - 0.2 * (1.0 - u * u);
    float gd = abs(y - gy);
    float ruff = 0.045 + 0.012 * sin(X * 70.0 + y * 40.0);
    float gar = 1.0 - smoothstep(ruff * 0.7, ruff, gd);
    col = mix(col, mix(vec3(0.98, 0.52, 0.08), vec3(1.0, 0.78, 0.15), step(0.5, fract(X * 9.0))) * (0.85 + 0.2 * n2), gar);
    // diya flames along the base: still at dusk, swaying in circles for the aarti
    vec2 lc = vec2(fract(X / 0.6) - 0.5, y - 1.05) * vec2(0.6, 1.0);
    float sway = aarti * 0.05;
    vec2 lp = lc - sway * vec2(cos(uT * 1.7 + floor(X / 0.6)), sin(uT * 1.7 + floor(X / 0.6)) * 0.6);
    float fl = flame(lp * vec2(1.0, 1.0)) * step(0.35, hash(floor(X / 0.6)));
    col += vec3(1.0, 0.55, 0.15) * exp(-length(lp) * 16.0) * (0.3 + 0.4 * aarti) * step(0.35, hash(floor(X / 0.6)));
    col = mix(col, vec3(1.0, 0.8, 0.35), fl);
    // incense smoke curling up in soft wisps
    for (int i = 0; i < 4; i++){
      float fi = float(i);
      float sx = -3.0 + fi * 2.1 + 0.3 * hash(fi);
      float h = y - 1.25;
      float x = sx + sin(h * 2.2 - uT * 0.7 + fi * 1.7) * 0.18 * h + sin(h * 5.0 - uT * 1.3 + fi) * 0.04 * h;
      float w = 0.02 + 0.06 * h;
      float wisp = (1.0 - smoothstep(w * 0.4, w, abs(X - x))) * smoothstep(0.0, 0.3, h) * (1.0 - smoothstep(1.5, 3.5, h));
      col = mix(col, vec3(0.9, 0.9, 0.92), wisp * 0.35);
    }
    // gangajal: streams of Ganga water running down and washing the scene
    float sc = fract(X * 1.4 + 0.3 * n);
    float stream = (1.0 - smoothstep(0.05, 0.12, abs(sc - 0.5))) * smoothstep(0.4, 0.6, hash(floor(X * 1.4 + 0.3 * n)));
    float flow = 0.6 + 0.4 * sin(y * 9.0 + uT * 7.0 + sc * 6.0);
    col = mix(col, vec3(0.82, 0.9, 0.98) * flow, stream * wash * (1.0 - smoothstep(9.5 - (uT - 23.0) * 2.0, 10.0 - (uT - 23.0) * 2.0, y)) * 0.6);
    local = vec2(X, y - uCy);
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz;
    vec2 g = abs(fract(xz / 0.9) - 0.5);
    col = mix(vec3(0.46, 0.44, 0.44), vec3(0.56, 0.53, 0.52), n) * (1.0 - 0.25 * (1.0 - smoothstep(0.0, 0.02, min(g.x, g.y))));
    col += vec3(1.0, 0.6, 0.25) * 0.08 * (0.6 + aarti);
    vec2 pc = floor(xz / 0.35);
    float ph = hash2(pc);
    vec2 pp = xz - (pc + 0.5) * 0.35;
    float a = ph * 6.28;
    pp = mat2(cos(a), -sin(a), sin(a), cos(a)) * pp;
    float petal = 1.0 - smoothstep(0.85, 1.0, length(vec2(pp.x / 0.03, pp.y / 0.06)));
    col = mix(col, ph < 0.9 ? vec3(0.98, 0.55, 0.1) : vec3(0.9, 0.2, 0.3), petal * step(0.82, ph));
    col = mix(col, vec3(0.75, 0.85, 0.95), wash * 0.35 * (0.6 + 0.4 * sin(length(xz) * 8.0 - uT * 3.0)));
    local = xz;
  } else {
    col = mix(vec3(0.1, 0.16, 0.42), vec3(0.06, 0.1, 0.3), n);
    local = vPos.xz;
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? vPos.y / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  col = mix(col, paint, smoothstep(0.0, 0.25, settle * 1.3 - drip * 0.6 - n * 0.25));
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
